import FilterListIcon from "@mui/icons-material/FilterList";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {
  Box,
  Chip,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslationHook } from "../../hooks";
import { Licenses } from "../../types/model/licenses/licenses";

interface LicensesTableProps {
  data: Licenses | null;
}

const LicensesTable = ({ data }: LicensesTableProps) => {

  const { t } = useTranslationHook();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    setPage(0);
  };

  if (!data) {
    return <Typography>Loading...</Typography>;
  }

  const filteredKeys = Object.keys(data).filter((key) => {
    if (!filters.module) return true;
    return key.toLowerCase().includes(filters.module.toLowerCase());
  });

  const visibleRows = filteredKeys.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredKeys.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("licenses.licenses")}
          </Typography>

          <Tooltip title={t("generic.filter")}>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>

        {showFilters && (
          <Grid2 container spacing={3} sx={{ mb: 2 }}>
            <Grid2 size={12}>
              <TextField
                name="module"
                label={t("generic.search")}
                value={filters.module ?? ""}
                onChange={handleFilterChange}
                fullWidth
              />
            </Grid2>
          </Grid2>
        )}

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("licenses.module")}</TableCell>
                <TableCell>{t("licenses.license")}</TableCell>
                <TableCell>{t("licenses.repository")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((key) => {
                const item = data[key];
                return (
                  <TableRow hover key={key}>
                    <TableCell sx={{ width: "50%" }}>
                      <Typography variant="body2">{key}</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "25%" }}>
                      <Chip label={item.licenses} color="primary" />
                    </TableCell>
                    <TableCell sx={{ width: "25%" }}>
                      <Chip
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {t("licenses.repository")}
                            <OpenInNewRoundedIcon fontSize="small" />
                          </Box>
                        }
                        color="secondary"
                        variant="outlined"
                        component="a"
                        href={item.repository}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ cursor: "pointer" }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredKeys.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default LicensesTable;